import { useState } from "react";
import { useTranslation } from "react-i18next";

const TYPES_CLIENT = [
  "Promoteur", "Administration", "Bureau d'Études",
  "Particulier", "Entreprise de Construction", "Autre"
];

const GV = {
  vert:"#2D7A2D", vertLight:"#E8F5E9", vertDark:"#1B5E20", vertMid:"#4CAF50",
  border:"#E0E0E0", gris:"#F5F5F5", dark:"#1A1A1A", grisMid:"#6B7280"
};

function genererCodeRef(locCode, userId) {
  const storageKey = `geo5_projets_${userId || "guest"}`;
  const projets = JSON.parse(localStorage.getItem(storageKey) || "[]");
  const now = new Date();
  const mois = String(now.getMonth() + 1).padStart(2, "0");
  const annee = String(now.getFullYear()).slice(-2);
  const prefix = `GEO_${locCode}_${mois}/${annee}_`;
  const n = projets.filter(p => {
    const ref = p.donneesProjet?.clientInfo?.codeRef || "";
    return ref.startsWith(prefix);
  }).length;
  return `${prefix}${String(n + 1).padStart(2, "0")}_V01`;
}

export default function FormulaireClient({ onDemarrer, onClose, pays: paysProp, userId }) {
  const { t } = useTranslation();
  const paysFixe = paysProp || "france";
  const [etapeForm, setEtapeForm] = useState(1);
  const [form, setForm] = useState({
    nomEntreprise: "", typeClient: "Promoteur",
    adresse: "", nomContact: "", prenomContact: "",
    tel: "", fax: "", email: "", mobile: "",
    wilaya: null, codeRef: "", budget: "",
    pays: paysFixe,
  });

  const sp = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Étape 1 complète = ville renseignée (obligatoire)
  const canSuivant = !!form.wilaya?.nom;
  const canDemarrer = canSuivant;

  const inpSt = {
    width: "100%", padding: "9px 12px", fontSize: 13,
    border: `1.5px solid ${GV.border}`, borderRadius: 8,
    color: GV.dark, background: "#fff", outline: "none", boxSizing: "border-box"
  };
  const lblSt = {
    fontSize: 11, fontWeight: 700, color: GV.grisMid,
    display: "block", marginBottom: 4, letterSpacing: 0.8, textTransform: "uppercase"
  };
  const sectionTitleSt = {
    fontSize: 12, fontWeight: 800, color: GV.vertDark, letterSpacing: 2,
    textTransform: "uppercase", marginBottom: 14,
    borderBottom: `2px solid ${GV.vertLight}`, paddingBottom: 6
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 700, display: "flex" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
      <div style={{
        position: "relative", marginLeft: "auto", width: 500,
        height: "100vh", overflowY: "auto", background: "#fff",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column"
      }}>

        {/* Header */}
        <div style={{ background: GV.vert, padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ color: "#fff", fontSize: 18, fontWeight: 800 }}>{t("formulaire.titre")}</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, marginTop: 2 }}>
              {etapeForm === 1
                ? "Étape 1/2 — Informations du projet *"
                : "Étape 2/2 — Informations client (optionnel)"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", gap: 5 }}>
              {[1, 2].map(n => (
                <div key={n} style={{
                  width: etapeForm === n ? 22 : 8, height: 8, borderRadius: 4,
                  background: etapeForm >= n ? "#fff" : "rgba(255,255,255,0.35)",
                  transition: "all 0.3s"
                }}/>
              ))}
            </div>
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#fff", fontSize: 20, cursor: "pointer" }}>✕</button>
          </div>
        </div>

        <div style={{ padding: "24px", flex: 1 }}>

          {/* ══ ÉTAPE 1 — Localisation + contexte normatif ══ */}
          {etapeForm === 1 && (
            <>
              {/* Contexte normatif FR */}
              <div style={{ marginBottom: 22 }}>
                <div style={sectionTitleSt}>{t("formulaire.contexte_normatif")}</div>
                <div style={{
                  padding: "12px 16px", borderRadius: 10,
                  border: "2px solid #1565C0",
                  background: "#EFF6FF",
                  display: "flex", alignItems: "center", gap: 12
                }}>
                  <div style={{ fontSize: 24 }}>🇫🇷</div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: "#1565C0" }}>
                      France
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>
                      EC7 · NF P 94 · EC8
                    </div>
                  </div>
                </div>
              </div>

              {/* Localisation */}
              <div style={{ marginBottom: 22 }}>
                <div style={sectionTitleSt}>{t("formulaire.localisation")}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={lblSt}>
                      {t("formulaire.ville_label")} <span style={{ color:"#DC2626" }}>*</span>
                    </label>
                    <input
                      value={form.wilaya?.nom || ""}
                      onChange={e => setForm(f => ({ ...f, wilaya: { nom: e.target.value, zone: f.wilaya?.zone || "", code: e.target.value.slice(0,4).toUpperCase() }, codeRef: genererCodeRef(e.target.value.slice(0,4).toUpperCase(), userId) }))}
                      placeholder={t("formulaire.ville_ph")}
                      style={{ ...inpSt, borderColor: !form.wilaya?.nom ? "#F59E0B" : "#1565C0", borderWidth: 2 }}
                    />
                    {!form.wilaya?.nom && (
                      <div style={{ fontSize: 11, color: "#F59E0B", marginTop: 4, fontWeight: 600 }}>
                        {t("formulaire.ville_requis")}
                      </div>
                    )}
                    <div style={{ marginTop: 10 }}>
                      <label style={lblSt}>{t("formulaire.zone_sismique_fr")}</label>
                      <select
                        value={form.wilaya?.zone || ""}
                        onChange={e => setForm(f => ({ ...f, wilaya: { ...f.wilaya, zone: e.target.value } }))}
                        style={{ ...inpSt, background: "#fff" }}
                      >
                        <option value="">-- Sélectionner la zone --</option>
                        <option value="1">Zone 1 — Très faible (Bassin parisien, Bretagne)</option>
                        <option value="2">Zone 2 — Faible (Massif Central, Normandie)</option>
                        <option value="3">Zone 3 — Modérée (Alpes Nord, Alsace)</option>
                        <option value="4">Zone 4 — Moyenne (Alpes Sud, Pyrénées)</option>
                        <option value="5">Zone 5 — Forte (Antilles)</option>
                      </select>
                    </div>
                  </div>

                  {form.wilaya?.zone && (
                    <div>
                      <label style={lblSt}>{t("formulaire.zone_sismique_fr")}</label>
                      <div style={{
                        padding: "9px 12px", background: GV.vertLight,
                        border: `1.5px solid ${GV.vertMid}`, borderRadius: 8,
                        fontSize: 14, fontWeight: 800, color: GV.vertDark
                      }}>
                        Zone {form.wilaya.zone}
                        <span style={{ fontSize: 11, fontWeight: 500, color: GV.grisMid, marginLeft: 8 }}>
                          {form.wilaya.zone === "1" ? "Très faible" :
                           form.wilaya.zone === "2" ? "Faible" :
                           form.wilaya.zone === "3" ? "Modérée" :
                           form.wilaya.zone === "4" ? "Moyenne" :
                           form.wilaya.zone === "5" ? "Forte" : ""}
                        </span>
                      </div>
                    </div>
                  )}

                  <div style={form.wilaya?.zone ? {} : { gridColumn: "1 / -1" }}>
                    <label style={lblSt}>{t("formulaire.code_ref")}</label>
                    <div style={{
                      padding: "9px 12px",
                      background: form.codeRef ? "#EDE7F6" : GV.gris,
                      border: `1.5px solid ${form.codeRef ? "#9C27B0" : GV.border}`,
                      borderRadius: 8, fontSize: 13, fontWeight: 700,
                      color: form.codeRef ? "#6A1B9A" : "#aaa",
                      fontFamily: "monospace"
                    }}>
                      {form.codeRef || t("formulaire.code_ref_ph")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Budget (optionnel) */}
              <div style={{ marginBottom: 28 }}>
                <div style={sectionTitleSt}>
                  {t("formulaire.budget_titre")}
                  <span style={{ fontSize: 10, fontWeight: 400, color: GV.grisMid, textTransform: "none", letterSpacing: 0, marginLeft: 8 }}>— optionnel</span>
                </div>
                <label style={lblSt}>{t("formulaire.budget_label")}</label>
                <input
                  type="number" min="0" step="100000"
                  value={form.budget} onChange={e => sp("budget", e.target.value)}
                  placeholder={t("formulaire.budget_ph")}
                  style={inpSt}
                />
                <div style={{ fontSize: 11, color: GV.grisMid, marginTop: 6, lineHeight: 1.5 }}>
                  {t("formulaire.budget_hint")}
                </div>
              </div>

              {/* Bouton Suivant */}
              <button
                onClick={() => canSuivant && setEtapeForm(2)}
                disabled={!canSuivant}
                style={{
                  width: "100%", padding: "14px", fontSize: 15, fontWeight: 800,
                  background: canSuivant ? `linear-gradient(135deg, ${GV.vert}, #00A651)` : "#ccc",
                  color: "#fff", border: "none", borderRadius: 10,
                  cursor: canSuivant ? "pointer" : "not-allowed",
                  boxShadow: canSuivant ? `0 4px 14px ${GV.vert}40` : "none",
                  transition: "all 0.2s"
                }}
              >
                Suivant → Informations client
              </button>
              {!canSuivant && (
                <div style={{ textAlign: "center", fontSize: 11, color: "#f59e0b", marginTop: 8 }}>
                  {t("formulaire.ville_requis")}
                </div>
              )}
            </>
          )}

          {/* ══ ÉTAPE 2 — Informations client (optionnel) ══ */}
          {etapeForm === 2 && (
            <>
              {/* Récap étape 1 */}
              {form.wilaya && (
                <div style={{ marginBottom: 20, padding: "10px 14px", background: GV.vertLight, border: `1px solid ${GV.vertMid}`, borderRadius: 10, fontSize: 12, color: GV.vertDark, fontWeight: 600 }}>
                  📍 {form.wilaya?.nom || form.wilaya?.code}
                  {form.wilaya?.zone ? ` · Zone ${form.wilaya.zone}` : ""}
                  {form.codeRef ? ` · ${form.codeRef}` : ""}
                </div>
              )}

              {/* Maître d'Ouvrage */}
              <div style={{ marginBottom: 22 }}>
                <div style={sectionTitleSt}>{t("formulaire.maitre_ouvrage")}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={lblSt}>{t("formulaire.nom_entreprise")}</label>
                    <input value={form.nomEntreprise} onChange={e => sp("nomEntreprise", e.target.value)}
                      placeholder={t("formulaire.nom_entreprise_ph")} style={inpSt} />
                  </div>
                  <div>
                    <label style={lblSt}>{t("formulaire.type_client")}</label>
                    <select value={form.typeClient} onChange={e => sp("typeClient", e.target.value)} style={{ ...inpSt, background: "#fff" }}>
                      {TYPES_CLIENT.map(tc => <option key={tc} value={tc}>{tc}</option>)}
                    </select>
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={lblSt}>{t("formulaire.adresse")}</label>
                    <input value={form.adresse} onChange={e => sp("adresse", e.target.value)}
                      placeholder={t("formulaire.adresse_ph")} style={inpSt} />
                  </div>
                  <div>
                    <label style={lblSt}>{t("formulaire.nom_contact")}</label>
                    <input value={form.nomContact} onChange={e => sp("nomContact", e.target.value)}
                      placeholder={t("formulaire.nom_contact")} style={inpSt} />
                  </div>
                  <div>
                    <label style={lblSt}>{t("formulaire.prenom")}</label>
                    <input value={form.prenomContact} onChange={e => sp("prenomContact", e.target.value)}
                      placeholder={t("formulaire.prenom")} style={inpSt} />
                  </div>
                  <div>
                    <label style={lblSt}>{t("formulaire.tel_fax")}</label>
                    <input value={form.tel} onChange={e => sp("tel", e.target.value)}
                      placeholder="023 XX XX XX" style={inpSt} />
                  </div>
                  <div>
                    <label style={lblSt}>{t("formulaire.mobile")}</label>
                    <input value={form.mobile} onChange={e => sp("mobile", e.target.value)}
                      placeholder="06X XX XX XX" style={inpSt} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={lblSt}>{t("formulaire.email")}</label>
                    <input type="email" value={form.email} onChange={e => sp("email", e.target.value)}
                      placeholder="contact@example.com" style={inpSt} />
                  </div>
                </div>
              </div>

              {/* Boutons étape 2 */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button
                  onClick={() => onDemarrer({ ...form })}
                  style={{
                    width: "100%", padding: "14px", fontSize: 15, fontWeight: 800,
                    background: `linear-gradient(135deg, ${GV.vert}, #00A651)`,
                    color: "#fff", border: "none", borderRadius: 10,
                    cursor: "pointer",
                    boxShadow: `0 4px 14px ${GV.vert}40`,
                    transition: "all 0.2s"
                  }}
                >
                  ✓ Enregistrer et Démarrer
                </button>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => setEtapeForm(1)}
                    style={{
                      flex: 1, padding: "11px", fontSize: 13, fontWeight: 700,
                      background: "#fff", color: GV.dark,
                      border: `1.5px solid ${GV.border}`, borderRadius: 10,
                      cursor: "pointer", transition: "all 0.2s"
                    }}
                  >
                    ← Retour
                  </button>
                  <button
                    onClick={() => onDemarrer({ ...form })}
                    style={{
                      flex: 1, padding: "11px", fontSize: 13, fontWeight: 700,
                      background: GV.gris, color: GV.grisMid,
                      border: `1.5px solid ${GV.border}`, borderRadius: 10,
                      cursor: "pointer", transition: "all 0.2s"
                    }}
                  >
                    Passer (facultatif)
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
